import { memo } from 'react';
import { IconLock } from '@tabler/icons-react';
import { Flex } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import {
  useMarketTeam,
  useMarketSpread,
  useMarketTotal,
  useMarketMoneyline,
  useIsMarketLockedByLiquidity,
} from '~/domains/market/state/selectors/marketCard.selectors';
import { useIsMarketSelected } from '~/domains/market/hooks/marketSelectionFactories';
import { usePriceFormat } from '~/domains/market/state/marketSettings.store';
import { getFormattedPrice } from '~/domains/market/utils/pricing';
import { formatTeamRecord } from '~/domains/contest/domains/gamecenter/domains/team/utils/formatTeamRecord';

import { MarketTeamIcon } from '../MarketIcon/MarketIcon';
import { RollingValue } from '../RollingValue/RollingValue';

import type { MarketTeamRowProps, MarketCellBaseProps, MarketType, TeamType } from './types';
import { useStyles } from './MarketCard.styles';

const TEAM_LOGO_SIZE = 28;

type MarketCellRenderProps = {
  market: MarketType;
  teamType: TeamType;
  marketId: string;
  isLocked: boolean;
  isSelected: boolean;
  lineDisplay?: string;
  odds?: string;
  onMarketClick: MarketTeamRowProps['onMarketClick'];
};

function MarketCellRender({
  market,
  teamType,
  marketId,
  isLocked,
  isSelected,
  lineDisplay,
  odds,
  onMarketClick,
}: MarketCellRenderProps) {
  const { classes, cx, theme } = useStyles();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLocked) {
      onMarketClick(e, market, teamType, marketId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    e.preventDefault();
    if (!isLocked) {
      onMarketClick(e, market, teamType, marketId);
    }
  };

  const handleLockedClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleLockedKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    e.preventDefault();
  };

  if (isLocked) {
    return (
      <div
        className={cx(classes.marketCell, classes.cellLocked)}
        data-market-cell
        data-locked
        role="button"
        tabIndex={-1}
        onClick={handleLockedClick}
        onKeyDown={handleLockedKeyDown}
      >
        <IconLock size={16} color={theme.colors.gray2[5]} />
      </div>
    );
  }

  return (
    <div
      className={cx(
        classes.marketCell,
        lineDisplay && classes.marketCellSplit,
        isSelected && classes.marketCellSelected
      )}
      onClick={handleClick}
      data-market-cell
      data-selected={isSelected}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {lineDisplay && (
        <Text
          variant={{ base: 'subhead-small', sm: 'subhead-medium' }}
          c={isSelected ? 'white' : 'gray2.9'}
        >
          {lineDisplay}
        </Text>
      )}
      <Text variant="headline-medium" c="blue.7">
        <RollingValue value={odds ?? '—'} />
      </Text>
    </div>
  );
}

const SpreadCell = memo(({ marketGroupId, teamType, onMarketClick }: MarketCellBaseProps) => {
  const spread = useMarketSpread(marketGroupId, teamType);
  const lockedByLiquidity = useIsMarketLockedByLiquidity(marketGroupId, 'spread', teamType);
  const isSelected = useIsMarketSelected(marketGroupId, 'spread', teamType);
  const priceFormat = usePriceFormat();

  return (
    <MarketCellRender
      market="spread"
      teamType={teamType}
      marketId={spread?.marketId}
      isLocked={!spread || lockedByLiquidity}
      isSelected={isSelected}
      lineDisplay={spread?.lineDisplay}
      odds={getFormattedPrice(spread?.price, priceFormat)}
      onMarketClick={onMarketClick}
    />
  );
});

SpreadCell.displayName = 'SpreadCell';

const TotalCell = memo(({ marketGroupId, teamType, onMarketClick }: MarketCellBaseProps) => {
  const total = useMarketTotal(marketGroupId, teamType);
  const lockedByLiquidity = useIsMarketLockedByLiquidity(marketGroupId, 'total', teamType);
  const isSelected = useIsMarketSelected(marketGroupId, 'total', teamType);
  const priceFormat = usePriceFormat();

  return (
    <MarketCellRender
      market="total"
      teamType={teamType}
      marketId={total?.marketId}
      isLocked={!total || lockedByLiquidity}
      isSelected={isSelected}
      lineDisplay={total?.lineDisplay}
      odds={getFormattedPrice(total?.price, priceFormat)}
      onMarketClick={onMarketClick}
    />
  );
});

TotalCell.displayName = 'TotalCell';

const MoneylineCell = memo(({ marketGroupId, teamType, onMarketClick }: MarketCellBaseProps) => {
  const moneyline = useMarketMoneyline(marketGroupId, teamType);
  const lockedByLiquidity = useIsMarketLockedByLiquidity(marketGroupId, 'moneyline', teamType);
  const isSelected = useIsMarketSelected(marketGroupId, 'moneyline', teamType);
  const priceFormat = usePriceFormat();

  return (
    <MarketCellRender
      market="moneyline"
      teamType={teamType}
      marketId={moneyline?.marketId}
      isLocked={!moneyline || lockedByLiquidity}
      isSelected={isSelected}
      odds={getFormattedPrice(moneyline?.price, priceFormat)}
      onMarketClick={onMarketClick}
    />
  );
});

MoneylineCell.displayName = 'MoneylineCell';

export const MarketTeamRow = memo(
  ({ marketGroupId, teamType, onMarketClick }: MarketTeamRowProps) => {
    const { classes, theme } = useStyles();

    const team = useMarketTeam(marketGroupId, teamType);

    if (!team) return null;

    const teamRecord = formatTeamRecord(team.record);

    return (
      <Flex align="center" gap={theme.other.spacing._16} w="100%" justify="space-between">
        <Flex className={classes.teamInfo} gap={theme.other.spacing._12}>
          <div className={classes.teamLogo}>
            <MarketTeamIcon teamIconUrl={team.iconUrl} size={TEAM_LOGO_SIZE} />
          </div>
          <Flex direction="column" justify="center" gap={theme.other.spacing._2}>
            <Text fw={800} variant="headline-medium">
              <span className={classes.teamNameShort}>{team.abbreviation}</span>
              <span className={classes.teamNameFull}>{team.name}</span>
            </Text>
            {teamRecord && (
              <Text variant="subhead-x-small" c="gray2.3">
                {teamRecord}
              </Text>
            )}
          </Flex>
        </Flex>

        {team.score !== undefined && (
          <Text ta="right" variant="headline-medium">
            {team.score}
          </Text>
        )}

        <Flex gap={theme.other.spacing._4} align="center">
          <SpreadCell
            marketGroupId={marketGroupId}
            teamType={teamType}
            onMarketClick={onMarketClick}
          />
          <TotalCell
            marketGroupId={marketGroupId}
            teamType={teamType}
            onMarketClick={onMarketClick}
          />
          <MoneylineCell
            marketGroupId={marketGroupId}
            teamType={teamType}
            onMarketClick={onMarketClick}
          />
        </Flex>
      </Flex>
    );
  }
);

MarketTeamRow.displayName = 'MarketTeamRow';
